// FocusHost — standalone renderer for one slot (or modal) of a Designer.
// Used by the focus-mode preview iframe: the embed URL carries
// `?focus=slot:<slotId>&mockState=<preset>` and `SceneDesignerTool` early-
// branches into this component instead of mounting the full configurator.
//
// Responsibilities:
//   - Fetch the Designer (same endpoint the configurator uses) so the theme,
//     active series, catalog, and saved slot mapping are all in scope.
//   - Apply theme tokens via <ThemeStyle> exactly like the configurator does.
//   - Build a mocked BlockHostContext via mockState.ts.
//   - Render just the requested slot through <Slot>, layered inside a small
//     chrome (.sd--layout-<layoutId> + the slot's container class so layout
//     CSS still applies).
//   - Listen for `postMessage` updates from the parent (slot mapping
//     overlay + DVar text overrides) so live editing in the dashboard
//     reflects without saving.
//
// Out of scope (intentional): the 3D viewer, real actions, modal stack,
// busy overlay. Focus mode is for previewing *content placement*, not
// behavior.

import { useEffect, useMemo, useRef, useState } from "react";
import { fetchDesigner } from "../api/catalogClient";
import { mergeSlotMappings, coerceSlotMapping } from "../blocks/slotMapping";
import { SeriesContext } from "../blocks/SeriesTileBlocks";
import { Slot } from "../blocks/Slot";
import type { BlockHostContext, BlockInstance, SlotMapping } from "../blocks/types";
import { composeCartModal } from "../components/composeCartModal";
import { composeEmailModal } from "../components/composeEmailModal";
import { ThemeStyle } from "../components/ThemeStyle";
import { getLayoutDefaultMapping, canonicalLayoutId } from "../layouts/registry";
import { useIsMobile } from "../useIsMobile";
import { buildMockHost, isMockStatePreset, type MockStatePreset } from "./mockState";
import { NotificationsPreview } from "./NotificationsPreview";
import { PREVIEW_SECTIONS, type PreviewSection } from "./sections";
import type { DesignerConfigResponse, DesignerSeriesDto } from "../types";

export interface FocusHostProps {
  designerId: string;
  baseUrl?: string;
  apiBaseUrl?: string;
  target?: string;
  stage?: "Development" | "Staging" | "Production";
  /** "slot:<slotId>" or "section:<id>". */
  focus: string;
  mockState: MockStatePreset;
  /** Short language code (e.g. "en", "dk") from the ?lang= the parent reloads
   *  with. Threaded into fetchDesigner so DVar text (labels, headers, copy) is
   *  resolved server-side into that language, and into composeCartModal so the
   *  cart's product-detail names match. Blank → primary. */
  lang?: string;
}

/** postMessage shape the parent (Blazor edit page) sends to the iframe.
 *  `slotMapping` overlays the saved mapping for this preview; `dvarOverrides`
 *  is a map of DVar id → primary text the parent has buffered but not saved,
 *  applied during text-block rendering. */
interface PreviewUpdateMessage {
  type: "sd-preview-update";
  slotMapping?: Record<string, BlockInstance[]> | null;
  dvarOverrides?: Record<number, string>;
}

function isPreviewUpdate(data: unknown): data is PreviewUpdateMessage {
  return typeof data === "object" && data !== null
    && (data as { type?: unknown }).type === "sd-preview-update";
}

export function FocusHost(props: FocusHostProps) {
  const [config, setConfig] = useState<DesignerConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live overlay from the parent — starts null, gets set by postMessage when
  // the operator edits in the dashboard. Saved mapping is used until the
  // overlay arrives.
  const [overlayMapping, setOverlayMapping] = useState<SlotMapping | null>(null);
  const [dvarOverrides, setDvarOverrides] = useState<Record<number, string>>({});

  // Reacts to the iframe width the focus editor's viewport toggle constrains,
  // so a section preview (e.g. the cart) picks the mobile body + layout exactly
  // as the live embed would at that width.
  const isMobile = useIsMobile();

  // ── Fetch the Designer ─────────────────────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchDesigner({
      baseUrl: props.baseUrl,
      designerId: props.designerId,
      target: props.target,
      stage: props.stage,
      // Resolve DVar text (labels, column headers, copy) into the previewed
      // language server-side — the parent reloads the iframe with a new ?lang=
      // when the picker changes, so the config re-fetches localized.
      lang: props.lang,
      signal: ctrl.signal,
    })
      .then(r => setConfig(r))
      .catch(err => {
        if (err.name === "AbortError") return;
        setError(err.message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [props.baseUrl, props.designerId, props.target, props.stage, props.lang]);

  // ── postMessage listener ───────────────────────────────────────────
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Strict origin gate — only accept messages from the dashboard origin
      // (same-origin parent). Cross-origin embeds shouldn't be in focus mode.
      if (e.origin !== window.location.origin) return;
      if (!isPreviewUpdate(e.data)) return;

      if (e.data.slotMapping !== undefined) {
        const coerced = coerceSlotMapping(e.data.slotMapping);
        setOverlayMapping(coerced);
      }
      if (e.data.dvarOverrides) {
        setDvarOverrides(e.data.dvarOverrides);
      }
    }
    window.addEventListener("message", onMessage);
    // Announce readiness so the parent can flush an initial state.
    if (window.parent !== window) {
      window.parent.postMessage({ type: "sd-preview-ready" }, window.location.origin);
    }
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // ── Resolve the slot mapping ──────────────────────────────────────
  // Resolution order: live overlay > saved override > layout default.
  // Canonicalise so a legacy slug (e.g. "editorial-glass") resolves to the
  // current layout's wrapper class + slot classes.
  const layoutId = canonicalLayoutId(config?.layoutTemplate);
  const mapping = useMemo(() => {
    const layoutDefault = getLayoutDefaultMapping(layoutId);
    const savedOverride = coerceSlotMapping(config?.slotMapping ?? null);
    // Saved override wins over layout default first…
    const withSaved = mergeSlotMappings(layoutDefault, savedOverride);
    // …then the live overlay wins over both for whichever slot ids it carries.
    return overlayMapping ? mergeSlotMappings(withSaved, overlayMapping) : withSaved;
  }, [layoutId, config?.slotMapping, overlayMapping]);

  // ── Active series (first series, since focus mode doesn't have a picker) ─
  const activeSeries: DesignerSeriesDto | null = config?.series[0] ?? null;

  // ── Build the mock host ───────────────────────────────────────────
  const host = useMemo(() => {
    if (!config) return null;
    return buildMockHost({
      preset: props.mockState,
      activeSeries,
      products: config.products,
      visibleCategories: config.categories,
      baseUrl: props.baseUrl,
      decimalSeparator: config.decimalSeparator,
    });
  }, [config, props.mockState, activeSeries, props.baseUrl]);

  // Apply DVar overrides post-hoc by walking the mapping tree. Block-level
  // `dvarId` references resolved on the server are already in `text` /
  // `label` props as plain strings; for unsaved edits, the parent sends the
  // override map so we can replace those resolved strings with the
  // buffered primary text. Tree walk mutates a fresh clone.
  const mappingWithOverrides = useMemo(() => {
    if (Object.keys(dvarOverrides).length === 0) return mapping;
    const cloned: SlotMapping = {};
    for (const [slotId, list] of Object.entries(mapping)) {
      cloned[slotId] = list.map(b => applyDvarOverrides(b, dvarOverrides));
    }
    return cloned;
  }, [mapping, dvarOverrides]);

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return <div className="sd-state sd-state--loading">Loading preview…</div>;
  }
  if (error) {
    return <div className="sd-state sd-state--error">Error: {error}</div>;
  }
  if (!config || !host) {
    return <div className="sd-state sd-state--empty">No data.</div>;
  }

  // focus=slot:<id> previews one slot; focus=section:<id> previews a whole
  // section (its member slots assembled). The prefix before the first ":"
  // selects the kind; everything after is the target id (slot ids themselves
  // contain colons, e.g. "modal:cart", so only the FIRST colon is the
  // kind separator).
  const firstColon = props.focus.indexOf(":");
  const focusKind = firstColon < 0 ? "unknown" : props.focus.slice(0, firstColon);
  const focusTarget = firstColon < 0 ? props.focus : props.focus.slice(firstColon + 1);

  if (focusKind === "section") {
    const section = PREVIEW_SECTIONS[focusTarget];
    if (!section) {
      return (
        <div className="sd-state sd-state--error">
          Section <code>{focusTarget}</code> isn't previewable.
        </div>
      );
    }
    return (
      <div className={"sd sd--layout-" + layoutId + " sd-focus sd-focus--section"}>
        {config.theme?.tokens && (
          <ThemeStyle tokens={config.theme.tokens} fontImportUrl={config.theme.fontImportUrl} />
        )}
        {section.compose === "cart" ? (
          // The real assembled cart modal — same composer the live configurator
          // uses, so the preview IS what ships (body + totals + footer).
          composeCartModal({
            mapping: mappingWithOverrides,
            host,
            isMobile,
            onClose: () => { /* preview: closing is inert */ },
            baseUrl: props.baseUrl,
            lang: props.lang,
            headlineTitle: activeSeries?.label ?? undefined,
          })
        ) : section.compose === "email" ? (
          // The real assembled email modal (header + form). composeEmailModal
          // returns the modal inner; wrap it in a non-dismissable backdrop
          // frame (same as single-slot modal focus) so it reads as a modal.
          <div className="sd-modal-backdrop sd-modal-backdrop--md sd-focus__modal-backdrop">
            <div className="sd-modal" onClick={e => e.stopPropagation()}>
              {composeEmailModal({
                mapping: mappingWithOverrides,
                host,
                onClose: () => { /* preview: closing is inert */ },
                fallbackTitle: undefined,
                fallbackSubtitle: activeSeries?.label ? `Finalizing specs for ${activeSeries.label}` : undefined,
              })}
            </div>
          </div>
        ) : (
          // Generic assembly: stack the member slots in the section's frame.
          <SectionStack
            section={section}
            mapping={mappingWithOverrides}
            host={host}
            layoutId={layoutId}
            activeSeries={activeSeries}
          />
        )}
      </div>
    );
  }

  if (focusKind !== "slot") {
    return (
      <div className="sd-state sd-state--error">
        Focus target <code>{props.focus}</code> isn't supported yet. Only <code>slot:&lt;id&gt;</code> and <code>section:&lt;id&gt;</code>.
      </div>
    );
  }

  // Modal slots ("modal:cart", "modal:help", …) wrap the slot's contents in
  // a stand-in modal frame so the preview shows what the operator would see
  // when the modal opens — not just the inner blocks bare on the page.
  const isModalSlot = focusTarget.startsWith("modal:");
  const slotEntries = mappingWithOverrides[focusTarget];
  const slotClass = slotClassFor(layoutId, focusTarget);

  // Render the slot inline (Fragment, no wrapper div) inside a wrapper that
  // carries the layout's slot class. Without `inline`, Slot wraps in
  // `<div className="sd-slot">` and the blocks become a single flex child
  // — the layout's gap/flex-direction rules then have nothing to operate on,
  // and the tool rail's buttons collapse to no-gap. Inline drops the wrapper
  // so the blocks land directly inside our slot div and the layout's CSS
  // sees them as siblings.
  // The series tiles slot renders once per series, bound to a SeriesContext.
  // In focus mode there's no live picker, so preview the blocks inside one
  // sample tile frame bound to the first real series — otherwise the
  // series-image / series-name blocks (which read the context) show nothing
  // and only a plain text CTA appears.
  const isSeriesTiles = focusTarget === "series-picker-tiles";
  // Notifications are toast-driven — no static render. Preview them via a
  // click-to-show list instead of rendering the (null) blocks bare.
  const isNotifications = focusTarget === "notifications";

  const slotElement = slotEntries == null ? (
    <div className="sd-state sd-state--empty">
      Slot <code>{focusTarget}</code> isn't in the resolved mapping.
    </div>
  ) : isNotifications ? (
    <NotificationsPreview entries={slotEntries} />
  ) : isSeriesTiles ? (
    <div className="sd-eg-series">
      <ul className="sd-eg-series__tiles">
        <li className="sd-eg-series__tile">
          <div className="sd-eg-series__tile-btn">
            <SeriesContext.Provider value={activeSeries}>
              <Slot slotId={focusTarget} mapping={slotEntries} host={host} inline />
            </SeriesContext.Provider>
          </div>
        </li>
      </ul>
    </div>
  ) : (
    <div className={slotClass}>
      <Slot
        slotId={focusTarget}
        mapping={slotEntries}
        host={host}
        inline
      />
    </div>
  );

  return (
    <div className={"sd sd--layout-" + layoutId + " sd-focus"}>
      {config.theme?.tokens && (
        <ThemeStyle tokens={config.theme.tokens} fontImportUrl={config.theme.fontImportUrl} />
      )}
      {isModalSlot ? (
        // Render the slot inside a non-dismissable modal frame. We omit the
        // backdrop's click-to-close so the operator can edit without
        // accidentally closing the preview — the host already prevents the
        // focus iframe from emitting modal close events.
        <div className="sd-modal-backdrop sd-modal-backdrop--md sd-focus__modal-backdrop">
          <div className="sd-modal" onClick={e => e.stopPropagation()}>
            <div className="sd-focus__modal-body">
              {slotElement}
            </div>
          </div>
        </div>
      ) : isViewportRegion(focusTarget) ? (
        // Viewport-overlay slots (tool rail, status card, left panel, selection
        // popup, series picker) are positioned RELATIVE TO THE VIEWPORT in the
        // real layout (e.g. the tool rail is absolute at the right edge). Render
        // them inside a replica `.sd__viewport` box so the real layout CSS
        // positions them exactly as it would in the live configurator — no
        // per-slot focus overrides. The box carries the layout class so
        // layout-scoped rules (.sd--layout-single-menu .sd__viewport …) apply.
        <div className="sd-focus__viewport-shell">
          <div className={"sd__viewport sd--layout-" + layoutId}>
            <div className="sd-focus__viewport-canvas" aria-hidden="true" />
            {slotElement}
          </div>
        </div>
      ) : (
        <div className={"sd-focus__inner sd-focus__slot--" + focusTarget}>
          {slotElement}
        </div>
      )}
    </div>
  );
}

/**
 * Generic section preview: stack the section's member slots top-to-bottom
 * inside a frame appropriate to the section kind (modal / screen / sheet). Used
 * for sections without a bespoke composer (series picker, mobile menu). Each
 * slot renders through <Slot> with its layout slot class so layout-scoped CSS
 * still applies; the series-tiles slot gets the same sample-tile treatment as
 * single-slot focus so its per-series blocks resolve.
 */
function SectionStack(props: {
  section: PreviewSection;
  mapping: SlotMapping;
  host: BlockHostContext;
  layoutId: string;
  activeSeries: DesignerSeriesDto | null;
}) {
  const { section, mapping, host, layoutId, activeSeries } = props;
  const frameClass =
    section.frame === "modal"
      ? "sd-modal-backdrop sd-modal-backdrop--md sd-focus__modal-backdrop"
      : section.frame === "sheet"
        ? "sd-focus__sheet"
        : "sd-focus__screen";

  const inner = (
    <div className="sd-focus__section-stack">
      {section.slotIds.map(slotId => {
        const entries = mapping[slotId];
        if (entries == null) return null; // slot not in the resolved mapping
        if (slotId === "series-picker-tiles") {
          return (
            <div key={slotId} className="sd-eg-series">
              <ul className="sd-eg-series__tiles">
                <li className="sd-eg-series__tile">
                  <div className="sd-eg-series__tile-btn">
                    <SeriesContext.Provider value={activeSeries}>
                      <Slot slotId={slotId} mapping={entries} host={host} inline />
                    </SeriesContext.Provider>
                  </div>
                </li>
              </ul>
            </div>
          );
        }
        return (
          <div key={slotId} className={slotClassFor(layoutId, slotId)}>
            <Slot slotId={slotId} mapping={entries} host={host} inline />
          </div>
        );
      })}
    </div>
  );

  if (section.frame === "modal") {
    return (
      <div className={frameClass}>
        <div className="sd-modal" onClick={e => e.stopPropagation()}>
          <div className="sd-focus__modal-body">{inner}</div>
        </div>
      </div>
    );
  }
  return <div className={frameClass}>{inner}</div>;
}

/**
 * Slots that in the real layout are positioned relative to the viewport (they
 * float over / anchor to the canvas edges) rather than sitting in normal flow.
 * For these the focus preview renders a replica `.sd__viewport` frame so the
 * real positioning CSS applies. Everything else (modal bodies, plain content
 * slots) renders in the flow container.
 */
function isViewportRegion(slotId: string): boolean {
  switch (slotId) {
    // The floating overlays anchored to the canvas edges — these are the ones
    // whose real positioning (absolute right / bottom-right) the shell restores.
    // Left panel + selection popup stay in the flow path (they read fine boxed
    // and need their own side-panel wrapper to anchor, which the slot alone
    // doesn't carry).
    case "tool-rail":
    case "status-card":
      return true;
    default:
      return false;
  }
}

/**
 * For known (layout, slotId) pairs return the layout's wrapper class so the
 * slot's contents inherit any layout-scoped CSS that depends on it. For
 * unknown pairs return an empty string — block-level CSS still applies.
 */
function slotClassFor(layoutId: string, slotId: string): string {
  if (layoutId === "single-menu") {
    switch (slotId) {
      case "left-panel":           return "sd-eg-leftpanel-slot";
      case "status-card":          return "sd-eg-status-slot";
      case "tool-rail":            return "sd-eg-toolrail";
      case "series-picker-intro":  return "sd-eg-series__intro";
      // Top-bar title slot doesn't have a meaningful standalone wrapper.
      default:                     return "";
    }
  }
  return "";
}

/**
 * Recursively replace resolved `text` / `label` props with their override
 * value when the block carries a matching `dvarId` / `labelDvarId`. The
 * override is the operator's unsaved primary-language text the dashboard
 * has buffered in DVarStringField.Value.
 */
function applyDvarOverrides(b: BlockInstance, overrides: Record<number, string>): BlockInstance {
  const props = (b.props ?? {}) as Record<string, unknown>;
  let mutated = false;
  const nextProps: Record<string, unknown> = { ...props };

  if (typeof props.dvarId === "number" && props.dvarId in overrides) {
    nextProps.text = overrides[props.dvarId];
    mutated = true;
  }
  if (typeof props.labelDvarId === "number" && props.labelDvarId in overrides) {
    nextProps.label = overrides[props.labelDvarId];
    mutated = true;
  }
  if (typeof props.unitSingularDvarId === "number" && props.unitSingularDvarId in overrides) {
    nextProps.unitSingular = overrides[props.unitSingularDvarId];
    mutated = true;
  }
  if (typeof props.unitPluralDvarId === "number" && props.unitPluralDvarId in overrides) {
    nextProps.unitPlural = overrides[props.unitPluralDvarId];
    mutated = true;
  }

  let nextChildren = b.children;
  if (b.children && b.children.length > 0) {
    nextChildren = b.children.map(c => applyDvarOverrides(c, overrides));
    if (nextChildren.some((c, i) => c !== b.children![i])) mutated = true;
  }

  // product-table columns: apply the column header override + recurse into each
  // column's cell blocks (they're BlockInstances too). Columns live on props.
  const cols = props.columns;
  if (Array.isArray(cols)) {
    const nextCols = cols.map(col => {
      if (!col || typeof col !== "object") return col;
      const c = col as Record<string, unknown>;
      let colMut = false;
      const nc: Record<string, unknown> = { ...c };
      if (typeof c.headerDvarId === "number" && c.headerDvarId in overrides) {
        nc.header = overrides[c.headerDvarId as number];
        colMut = true;
      }
      if (Array.isArray(c.blocks)) {
        const nb = (c.blocks as BlockInstance[]).map(cb => applyDvarOverrides(cb, overrides));
        if (nb.some((x, i) => x !== (c.blocks as BlockInstance[])[i])) { nc.blocks = nb; colMut = true; }
      }
      return colMut ? nc : col;
    });
    if (nextCols.some((x, i) => x !== cols[i])) { nextProps.columns = nextCols; mutated = true; }
  }

  return mutated ? { ...b, props: nextProps, children: nextChildren } : b;
}
